/**
 * Cpu2dStrategy — the StackViewport-backed RenderStrategy (no sampler3D).
 *
 * The weak-GPU / non-reconstructable fallback for the unified viewport path:
 * renders the series as a Cornerstone STACK viewport over the per-plane series
 * imageIds, exactly how the legacy `StackViewport.tsx` renders today (demo
 * COR/SAG are separate reconstructed series shown as stacks — NOT on-the-fly
 * reslices; reslicing is out of scope, see #575). Mirrors `GpuVolumeStrategy`'s
 * contract and statelessness (design spec §9.6): the strategy ONLY rasterizes —
 * it owns no pane state, navigation rules, tools, segmentation, atoms, or
 * sibling-pane knowledge. Navigation semantics (anatomicSign, clamping to the
 * slot's range, IPP-vs-index choice) live in NavigationController/SessionGeometry.
 *
 * Per-call notes:
 *  - attach     → idempotent enableElement(STACK) + setStack(imageIds). The
 *                 imageIds are injected at construction (list-index order, the
 *                 same `buildS3ImageIds` order the session geometry indexes).
 *  - showIndex  → setImageIdIndex with a get/setViewPresentation pin around it:
 *                 Cornerstone's setImageIdIndex runs an internal resetCamera
 *                 that drifts toward fit-to-window when adjacent slices differ
 *                 in geometry — the "scroll resets the zoom" annotator bug the
 *                 legacy StackViewport pinned the same way.
 *  - showWorld  → cornerstone.utilities.getClosestStackImageIndexForPoint
 *                 (world → nearest stack index) then showIndex. Stack viewports
 *                 have no jumpToWorld.
 *  - camera     → zoom only. StackViewport pan restore was never supported on
 *                 the legacy path (ProtocolViewport wires onZoomChange only);
 *                 a non-zero pan is DROPPED — logged, never swallowed.
 *  - setBlend   → not supported on a 2D stack; logged no-op for MIP/MinIP.
 */
import type { RenderingEngine, StackViewport } from "@cornerstonejs/core";
import { getCornerstone, getEnums } from "../../utils/dicomLoader";
import { logger } from "../../utils/logger";
import type {
  BlendMode,
  CameraState,
  PlaneOrientation,
  RenderStrategyKind,
  WorldPos,
} from "../types";
import type { RenderStrategy } from "./RenderStrategy";
import { applyRadiologicalFlip } from "../radiologicalOrientation";

export interface Cpu2dStrategyDeps {
  viewportId: string;
  /** Returns the shared rendering engine (same dep shape as GpuVolumeStrategy). */
  getEngine: () => RenderingEngine;
  /** The series imageIds in list-index order (`buildS3ImageIds(instances)` —
   *  the same order the SessionGeometry's indices refer to). */
  imageIds: readonly string[];
  /** Gate for the radiological-L/R camera flip — same contract and same owner
   *  as GpuVolumeStrategy's. A stack pane needs it too: a mirrored axial that
   *  fell back to stack (non-reconstructable, weak GPU) is just as laterality-
   *  reversed as the volume rendering of it would be. */
  shouldFlipHorizontal?: () => boolean;
}

export class Cpu2dStrategy implements RenderStrategy {
  readonly kind: RenderStrategyKind = "Cpu2d";

  private readonly deps: Cpu2dStrategyDeps;

  constructor(deps: Cpu2dStrategyDeps) {
    this.deps = deps;
  }

  /**
   * Live Cornerstone viewport for this strategy's id, narrowed to the concrete
   * StackViewport type (a STACK-typed viewport is a StackViewport at runtime —
   * same cast pattern as GpuVolumeStrategy.getViewport). Null until attach.
   */
  private getViewport(): StackViewport | null {
    const engine = this.deps.getEngine();
    const vp = engine.getViewport(this.deps.viewportId);
    return (vp as unknown as StackViewport) ?? null;
  }

  async attach(element: HTMLDivElement, _volumeId: string): Promise<void> {
    // The volumeId arg is part of the RenderStrategy contract (the GPU
    // strategy binds it); a stack renders from imageIds directly. The source
    // volume still exists in CPU cache for the seg labelmap — owned by the
    // mount, not this strategy.
    const Enums = getEnums();
    const engine = this.deps.getEngine();

    // Idempotent enableElement so a re-attach (StrictMode double-mount,
    // strategy swap onto the same slot) doesn't trip "viewport already exists".
    if (!engine.getViewport(this.deps.viewportId)) {
      engine.enableElement({
        viewportId: this.deps.viewportId,
        type: Enums.ViewportType.STACK,
        element,
        defaultOptions: {
          background: [0, 0, 0] as [number, number, number],
        },
      });
    }

    const vp = this.getViewport();
    if (!vp || typeof vp.setStack !== "function") {
      // enableElement succeeded but the viewport isn't a stack — config bug,
      // surface it instead of rendering a silent black pane.
      throw new Error(
        `[Cpu2dStrategy] viewport ${this.deps.viewportId} is not a STACK viewport after enable`,
      );
    }
    await vp.setStack([...this.deps.imageIds]);

    // #coronal-lr: same radiological-L/R flip the GPU path applies, after the
    // stack is set (setStack resets the camera). Idempotent across re-attach.
    applyRadiologicalFlip(
      vp,
      this.deps.shouldFlipHorizontal?.() ?? false,
      `Cpu2dStrategy ${this.deps.viewportId}`,
    );
  }

  showWorld(world: WorldPos, _orientation: PlaneOrientation): void {
    // Stack viewports have no jumpToWorld; project the world point onto the
    // stack's slice geometry and land on the nearest index. The orientation
    // arg is unused — this stack IS a single fixed plane (the reconstructed
    // per-plane series), so there is no reslice choice to make.
    const vp = this.getViewport();
    if (!vp) return;
    let index: number | null = null;
    try {
      const cornerstone = getCornerstone();
      index = cornerstone.utilities.getClosestStackImageIndexForPoint(
        [world[0], world[1], world[2]],
        vp,
      );
    } catch (err) {
      logger.warn("[Cpu2dStrategy] getClosestStackImageIndexForPoint failed:", err);
      return;
    }
    if (index === null || index === undefined || !Number.isFinite(index)) {
      // No projectable slice (e.g. metadata not hydrated yet). The caller's
      // NavigationController already prefers showIndex when IPP is missing, so
      // this is the cross-plane world-jump path — warn, don't snap to a guess.
      logger.warn(
        "[Cpu2dStrategy] showWorld could not resolve a stack index viewportId=%s world=%o",
        this.deps.viewportId,
        world,
      );
      return;
    }
    this.showIndex(index);
  }

  showIndex(listIndex: number): void {
    const vp = this.getViewport();
    if (!vp || typeof vp.setImageIdIndex !== "function") return;
    const total = this.deps.imageIds.length;
    const clamped = Math.max(0, Math.min(total - 1, listIndex));
    try {
      // Pin zoom/pan across the slice change: setImageIdIndex runs an internal
      // resetCamera + presentation round-trip that drifts toward fit-to-window
      // when adjacent slices differ in geometry (spacing/rows/cols) — the
      // legacy StackViewport pinned it with the same get/setViewPresentation
      // pattern (see resizeAndRestoreViewports).
      const pres = vp.getViewPresentation?.();
      vp.setImageIdIndex(clamped);
      if (pres) {
        vp.setViewPresentation?.(pres);
        vp.render?.();
      }
    } catch (err) {
      logger.warn("[Cpu2dStrategy] setImageIdIndex failed index=%d:", clamped, err);
    }
  }

  getLandedWorld(): WorldPos | null {
    // Pure read — the stack camera's focal point lies on the current image
    // plane in world space (same read as GpuVolumeStrategy).
    const vp = this.getViewport();
    const cam = vp?.getCamera?.();
    return cam?.focalPoint ? [cam.focalPoint[0], cam.focalPoint[1], cam.focalPoint[2]] : null;
  }

  getCamera(): CameraState {
    const vp = this.getViewport();
    if (!vp) return { zoom: 1, pan: [0, 0] };
    const zoom = typeof vp.getZoom === "function" ? vp.getZoom() : 1;
    // Pan is intentionally reported as [0,0]: the legacy stack path never
    // mirrored or restored pan (zoom-only surface), and reporting a live pan
    // here while setCamera drops it would make the session snapshot lie on
    // strategy swap. Zoom-only in BOTH directions keeps the contract honest.
    return { zoom, pan: [0, 0] };
  }

  setCamera({ zoom, pan }: CameraState): void {
    const vp = this.getViewport();
    if (!vp) return;
    if (typeof vp.setZoom === "function") vp.setZoom(zoom);
    if (pan[0] !== 0 || pan[1] !== 0) {
      // Pan restore is not supported on the stack path (legacy parity —
      // ProtocolViewport wires zoom-only for stacks). Dropped, not swallowed.
      logger.warn(
        "[Cpu2dStrategy] setCamera dropped non-zero pan [%d, %d] viewportId=%s (stack path is zoom-only)",
        pan[0],
        pan[1],
        this.deps.viewportId,
      );
    }
    vp.render?.();
  }

  setBlend(mode: BlendMode, slabThicknessMm?: number): void {
    if (mode === "composite") return; // composite is the stack's native render
    // MIP/MinIP need a volume actor; a 2D stack can't honor them. Logged so a
    // misrouted MIP pane (SliceDependentGrid is volume-only by design) is
    // observable instead of silently rendering composite.
    logger.warn(
      "[Cpu2dStrategy] setBlend(%s, %s) unsupported on the stack path viewportId=%s — rendering composite",
      mode,
      String(slabThicknessMm),
      this.deps.viewportId,
    );
  }

  dispose(): void {
    // Disable only THIS viewport — NEVER engine.destroy(): the Workspace owns
    // engine lifecycle; destroying it would orphan sibling panes (mirrors
    // GpuVolumeStrategy.dispose).
    try {
      const engine = this.deps.getEngine();
      engine.disableElement(this.deps.viewportId);
    } catch (err) {
      logger.debug("[Cpu2dStrategy] dispose disableElement failed:", err);
    }
  }
}
